import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "../../api/client";
import NavBar from "../../components/NavBar";
import useAuthStore from "../../store/authStore";

function StatCard({ label, value, sublabel }) {
  return (
    <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm dark:shadow-none p-5">
      <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
        {label}
      </p>
      <p className="text-3xl font-bold text-gray-900 dark:text-white">
        {value}
      </p>
      {sublabel && <p className="text-xs text-gray-400 mt-1">{sublabel}</p>}
    </div>
  );
}

function ActivityChart({ data }) {
  const max = Math.max(1, ...data.map((d) => d.flows));
  return (
    <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm dark:shadow-none p-5">
      <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">
        Daily activity (last 30 days)
      </p>
      <div className="flex items-end gap-1 h-40">
        {data.map((d) => {
          const height = (d.flows / max) * 100;
          return (
            <div
              key={d.date}
              className="flex-1 flex flex-col items-center justify-end group relative"
              title={`${d.date}: ${d.flows} flows`}
            >
              <div
                className="w-full bg-brand-violet rounded-t opacity-80 group-hover:opacity-100 transition"
                style={{
                  height: `${height}%`,
                  minHeight: d.flows > 0 ? "2px" : "0",
                }}
              />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-2 text-xs text-gray-400">
        <span>{data[0]?.date.slice(5)}</span>
        <span>{data[Math.floor(data.length / 2)]?.date.slice(5)}</span>
        <span>{data[data.length - 1]?.date.slice(5)}</span>
      </div>
    </div>
  );
}

function HealthRow({ label, timestamp, now }) {
  if (!timestamp) {
    return (
      <div className="flex justify-between py-2 border-b border-gray-100 dark:border-white/5 last:border-0">
        <span className="text-sm text-gray-600 dark:text-gray-300">
          {label}
        </span>
        <span className="text-sm text-gray-400">never</span>
      </div>
    );
  }
  const eventTime = new Date(timestamp);
  const minutesAgo = Math.round((now - eventTime) / 60000);
  let display;
  if (minutesAgo < 1) display = "just now";
  else if (minutesAgo < 60) display = `${minutesAgo}m ago`;
  else if (minutesAgo < 60 * 24)
    display = `${Math.round(minutesAgo / 60)}h ago`;
  else display = `${Math.round(minutesAgo / 60 / 24)}d ago`;

  return (
    <div className="flex justify-between py-2 border-b border-gray-100 dark:border-white/5 last:border-0">
      <span className="text-sm text-gray-600 dark:text-gray-300">{label}</span>
      <span className="text-sm text-gray-900 dark:text-gray-100">
        {display}
      </span>
    </div>
  );
}

export default function AdminDashboard() {
  const seekerUser = useAuthStore((s) => s.seekerUser);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = () =>
      api
        .get("/admin/stats")
        .then(({ data }) => {
          setStats(data);
          setError(null);
        })
        .catch((err) => {
          setError(err.response?.status === 403 ? "forbidden" : "error");
        })
        .finally(() => setLoading(false));

    fetchStats();
    const interval = setInterval(fetchStats, 30_000);
    return () => clearInterval(interval);
  }, []);

  // Client-side admin gate — server enforces too, but this prevents
  // a flash of the page for non-admin users who somehow reach the URL.
  if (seekerUser && !seekerUser.is_admin) {
    return <Navigate to="/app/dashboard" replace />;
  }

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-brand-light-bg dark:bg-brand-dark-bg">
        <NavBar />
        <div className="max-w-6xl mx-auto px-6 py-12 text-center text-gray-400">
          Loading…
        </div>
      </div>
    );
  }

  if (error === "forbidden") {
    return <Navigate to="/app/dashboard" replace />;
  }

  if (error || !stats) {
    return (
      <div className="min-h-screen bg-brand-light-bg dark:bg-brand-dark-bg">
        <NavBar />
        <div className="max-w-6xl mx-auto px-6 py-12 text-center">
          <p className="text-red-500">Failed to load admin stats.</p>
        </div>
      </div>
    );
  }

  const now = new Date();
  const generatedAt = new Date(stats.generated_at);
  const generatedAgo = Math.round((now - generatedAt) / 1000);

  return (
    <div className="min-h-screen bg-brand-light-bg dark:bg-brand-dark-bg">
      <NavBar />
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header with health pill */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Admin
            </h1>
            <p className="text-xs text-gray-400 mt-1">
              Internal observability · updated {generatedAgo}s ago
            </p>
          </div>
          <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
              stats.all_systems_operational
                ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400"
                : "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400"
            }`}
          >
            <span
              className={`w-2 h-2 rounded-full ${
                stats.all_systems_operational ? "bg-green-500" : "bg-amber-500"
              }`}
            />
            {stats.all_systems_operational
              ? "All systems operational"
              : "Reduced activity"}
          </div>
        </div>

        {/* Top counts */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <StatCard
            label="Total users"
            value={stats.total_users}
            sublabel={`${stats.users.employers_agency} agencies, ${stats.users.employers_direct} employers`}
          />
          <StatCard label="Active 30d" value={stats.active_users_30d} />
          <StatCard label="Active positions" value={stats.active_positions} />
          <StatCard label="Flows today" value={stats.flows_today} />
        </div>

        {/* Activity chart */}
        <div className="mb-6">
          <ActivityChart data={stats.daily_activity} />
        </div>

        {/* System health */}
        <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm dark:shadow-none p-5">
          <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
            System health
          </p>
          <div className="space-y-0">
            <HealthRow
              label="Last user signup"
              timestamp={stats.health.last_user_signup}
              now={now}
            />
            <HealthRow
              label="Last application submitted"
              timestamp={stats.health.last_application_submitted}
              now={now}
            />
            <HealthRow
              label="Last status push"
              timestamp={stats.health.last_status_push}
              now={now}
            />
            <HealthRow
              label="Last verification completed"
              timestamp={stats.health.last_verification_completed}
              now={now}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
